import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  Mail,
  Check,
  X,
  ChevronDown,
  Send,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { SectionLabel } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { IMAGES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollToApply = () => {
  const el = document.getElementById("apply");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const AD_SOURCES = {
  craigslist: "Craigslist",
  indeed: "Indeed",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  google: "Google",
  reddit: "Reddit",
};

const scrollToQualify = () => {
  const el = document.getElementById("qualify");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TEACHES = [
  "Business model and business-building fundamentals",
  "Prospecting systems",
  "Client acquisition",
  "Pipeline development",
  "Client relationship management",
  "Operating processes",
  "CRM and technology workflows",
  "Financial and business discipline",
  "Organization and productivity",
  "Leadership and team development",
  "Mentoring and training",
  "Building a business that grows beyond your individual production",
];

const CCA_PROVIDES = [
  "The business model",
  "Initial training",
  "The CCA Operating System",
  "Technology",
  "Ongoing operational support",
  "A roadmap for growth and advancement",
];

const YOU_PROVIDE = [
  "Sales ability",
  "Initiative",
  "Persistence",
  "Accountability",
  "The willingness to learn the rest of the game",
];

const FLOW = [
  { label: "You", detail: "Successful Sales Professional" },
  { label: "CCA", detail: "Training + Business Model + Systems + Technology + Support" },
  { label: "Your Clients", detail: "Independent commercial insurance agents" },
  { label: "Recurring Revenue", detail: "Clients pay CCA for recurring Pipeline Development Services" },
  { label: "Growth", detail: "Build clients, develop leadership, advance, earn based on growth" },
];

const COMP = [
  { role: "PDS", note: "Pipeline Development Specialist", value: "40%", sub: "of top-line revenue" },
  { role: "Supervisor", note: "Override", value: "20%", sub: "override" },
  { role: "Manager", note: "Override", value: "7%", sub: "override" },
  { role: "Director", note: "Override", value: "1%", sub: "override" },
  { role: "VP", note: "Override", value: "0.25%", sub: "override" },
];

const PATH = [
  { n: "01", role: "Associate", body: "Everyone who joins the Association is an Associate. You begin by learning the business model and launching your business." },
  { n: "02", role: "PDS", body: "Build and manage your own client base." },
  { n: "03", role: "Supervisor", body: "After demonstrating that you can successfully build your own business and are ready to help others, you may be invited to mentor new Associates." },
  { n: "04", role: "Manager", body: "Develop and lead Supervisors and their teams." },
  { n: "05", role: "Director", body: "Develop multiple Managers and expand your organization." },
  { n: "06", role: "VP", body: "Continue developing leaders and building the organization." },
];

const FIT = [
  "You are already successful in sales.",
  "You are ideally earning $100K+ in sales.",
  "You want to build something of your own.",
  "You are entrepreneurial.",
  "You are persistent.",
  "You can handle rejection.",
  "You are comfortable prospecting.",
  "You can manage your own schedule.",
  "You are willing to learn skills beyond sales.",
  "You take responsibility for your results.",
];

const NOT_FIT = [
  "You have little or no sales experience.",
  "You are looking for a salary or hourly wage.",
  "You need someone to tell you when to work.",
  "You want guaranteed income.",
  "You are looking for a get-rich-quick opportunity.",
  "You think great sales skills are all you need to build a business.",
  "You give up easily.",
  "You need someone else to motivate you.",
];

const NEXT = [
  { n: "01", title: "We Review", body: "We personally review every submission." },
  { n: "02", title: "We Decide", body: "If we don't believe you're a fit, we'll tell you. If we believe you may be a fit, we'll invite you to schedule a private interview." },
  { n: "03", title: "We Talk", body: "The interview is an opportunity for us to learn more about you—and for you to determine whether CCA is right for you." },
];

function ApplyButton({ children, testid, variant = "light" }) {
  const base =
    "group inline-flex items-center gap-2 px-7 py-4 font-mono text-[12px] uppercase tracking-[0.12em] transition-colors cursor-pointer";
  const styles =
    variant === "light"
      ? "border border-paper bg-paper text-ink hover:bg-transparent hover:text-paper"
      : "border border-ink bg-ink text-paper hover:bg-transparent hover:text-ink";
  return (
    <button type="button" onClick={scrollToApply} data-testid={testid} className={`${base} ${styles}`}>
      <Mail className="h-4 w-4" />
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </button>
  );
}

const APPLY_FIELD =
  "rounded-none border-line bg-white focus-visible:ring-1 focus-visible:ring-navy focus-visible:border-navy";

function ApplyForm() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", application_code: (searchParams.get("code") || "").toUpperCase(), reasons: "", background: "", why: "", company_website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.reasons) {
      toast.error("Please add your name, email, phone, and your three strongest reasons.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/opportunity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
      toast.success("Application received — thank you.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="apply-success" className="border border-line bg-white p-8 md:p-10">
        <CheckCircle2 className="h-10 w-10 text-navy" strokeWidth={1.25} />
        <h3 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">
          Thank you — we've got it.
        </h3>
        <p className="mt-4 leading-relaxed text-subtext">
          We personally review every submission. If we believe you may be a fit, we'll reach out to
          arrange a private interview. There is no obligation to join, and no guarantee we'll invite you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="apply-form" className="border border-line bg-white p-8 md:p-10">
      <input
        type="text"
        name="company_website"
        value={form.company_website}
        onChange={set("company_website")}
        tabIndex={-1}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-form-type="other"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">Tell us why we should consider you</p>
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="a-name">Full name *</Label>
          <Input id="a-name" maxLength={120} data-testid="apply-name" className={APPLY_FIELD} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="a-email">Email *</Label>
          <Input id="a-email" type="email" maxLength={200} data-testid="apply-email" className={APPLY_FIELD} value={form.email} onChange={set("email")} placeholder="jane@email.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="a-phone">Phone *</Label>
          <Input id="a-phone" type="tel" maxLength={40} required data-testid="apply-phone" className={APPLY_FIELD} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="a-linkedin">LinkedIn profile</Label>
          <Input id="a-linkedin" type="text" maxLength={200} data-testid="apply-linkedin" className={APPLY_FIELD} value={form.linkedin} onChange={set("linkedin")} placeholder="linkedin.com/in/janedoe" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="a-code">Application code</Label>
          <Input id="a-code" type="text" maxLength={10} data-testid="apply-code" className={APPLY_FIELD} value={form.application_code} onChange={set("application_code")} placeholder="Place application code here" />
          <p className="text-xs text-subtext">Shown in the ad you responded to (e.g., PH1).</p>
        </div>
      </div>
      <div className="mt-6 space-y-2">
        <Label htmlFor="a-reasons">1. Your three strongest reasons we should consider you *</Label>
        <Textarea id="a-reasons" rows={4} maxLength={3000} data-testid="apply-reasons" className={APPLY_FIELD} value={form.reasons} onChange={set("reasons")} placeholder="Reason one…  Reason two…  Reason three…" />
      </div>
      <div className="mt-6 space-y-2">
        <Label htmlFor="a-background">2. A brief description of your sales background and what you've accomplished</Label>
        <Textarea id="a-background" rows={4} maxLength={3000} data-testid="apply-background" className={APPLY_FIELD} value={form.background} onChange={set("background")} placeholder="Your track record…" />
      </div>
      <div className="mt-6 space-y-2">
        <Label htmlFor="a-why">3. Why you want to build your own business</Label>
        <Textarea id="a-why" rows={4} maxLength={3000} data-testid="apply-why" className={APPLY_FIELD} value={form.why} onChange={set("why")} placeholder="What's driving you…" />
      </div>
      <p className="mt-4 text-sm text-subtext">Keep it concise.</p>
      <button
        type="submit"
        data-testid="apply-submit"
        disabled={loading}
        className="group mt-8 inline-flex items-center gap-2 border border-ink bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.12em] text-paper transition-colors hover:bg-transparent hover:text-ink disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? "Submitting" : "Submit Application"}
      </button>
    </form>
  );
}

const QUIZ = [
  { id: "success", text: "I'm already successful in sales." },
  { id: "income", text: "I'm earning — or have earned — $100K+ in sales." },
  { id: "own", text: "I want to build something of my own." },
  { id: "prospect", text: "I'm comfortable prospecting and handling rejection." },
  { id: "schedule", text: "I can manage my own schedule and own my results." },
  { id: "learn", text: "I'm willing to learn business skills beyond selling." },
];

function QualifyQuiz() {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const answered = Object.keys(answers).length === QUIZ.length;

  const setAns = (id, val) => {
    setAnswers((a) => ({ ...a, [id]: val }));
    setResult(null);
  };
  const evaluate = () => {
    const yes = QUIZ.filter((q) => answers[q.id]).length;
    if (!answers.success) setResult("not");
    else if (yes >= 5) setResult("strong");
    else setResult("maybe");
  };
  const reset = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div data-testid="qualify-quiz" className="border border-line bg-white p-8 md:p-10">
      <div className="flex items-center gap-3">
        <span className="h-1.5 w-1.5 rounded-full bg-clay" />
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">30-Second Self-Check</p>
      </div>
      <h3 className="mt-4 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">Be honest with yourself.</h3>

      <div className="mt-8 divide-y divide-line border-y border-line">
        {QUIZ.map((q) => (
          <div key={q.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-ink">{q.text}</p>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                data-testid={`quiz-${q.id}-yes`}
                onClick={() => setAns(q.id, true)}
                className={`border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                  answers[q.id] === true ? "border-navy bg-navy text-paper" : "border-line text-subtext hover:border-ink hover:text-ink"
                }`}
              >
                Yes
              </button>
              <button
                type="button"
                data-testid={`quiz-${q.id}-no`}
                onClick={() => setAns(q.id, false)}
                className={`border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
                  answers[q.id] === false ? "border-ink bg-ink text-paper" : "border-line text-subtext hover:border-ink hover:text-ink"
                }`}
              >
                No
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Score */}
      <div className="mt-8">
        {!result ? (
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              data-testid="quiz-submit"
              onClick={evaluate}
              disabled={!answered}
              className="inline-flex items-center gap-2 border border-ink bg-ink px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.12em] text-paper transition-colors hover:bg-transparent hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
            >
              See My Score <ArrowRight className="h-4 w-4" />
            </button>
            {!answered && (
              <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-subtext">Answer all six to continue</span>
            )}
          </div>
        ) : (
          <div data-testid="quiz-result" className="border-t border-line pt-8">
            {result === "strong" && (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-navy">Strong Fit</p>
                <p className="mt-3 font-display text-2xl font-medium leading-snug tracking-tight text-ink">
                  You sound like exactly the kind of professional we want to hear from.
                </p>
              </>
            )}
            {result === "maybe" && (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">Possible Fit</p>
                <p className="mt-3 font-display text-2xl font-medium leading-snug tracking-tight text-ink">
                  You may have what it takes — if you're ready to build.
                </p>
              </>
            )}
            {result === "not" && (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-subtext">Probably Not a Fit</p>
                <p className="mt-3 font-display text-2xl font-medium leading-snug tracking-tight text-ink">
                  Sales mastery is our entry requirement — not something we teach.
                </p>
                <p className="mt-4 leading-relaxed text-subtext">
                  CCA helps proven sales professionals build a business. If that's not where you are yet, this opportunity likely isn't the right one for you.
                </p>
              </>
            )}
            <button
              type="button"
              data-testid="quiz-reset"
              onClick={reset}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.12em] text-subtext underline-offset-4 hover:text-ink hover:underline"
            >
              Start Over
            </button>
          </div>
        )}
      </div>

      {/* Application step */}
      <div className="mt-10 border-t border-line pt-8">
        <p className="mb-8 max-w-xl leading-relaxed text-subtext">
          If that sounds like you, don't send a résumé — tell us why we should consider you.
        </p>
        <ApplyButton testid="quiz-email-cta" variant="dark">Tell Us Why We Should Consider You</ApplyButton>
      </div>
    </div>
  );
}

/* 1. HERO */
function Hero() {
  const ref = useRef(null);
  const [params] = useSearchParams();
  const rawSrc = params.get("src") || params.get("utm_source") || "";
  const adLabel = AD_SOURCES[rawSrc.toLowerCase()] || null;
  const adNote = rawSrc
    ? adLabel
      ? `Responding to our ${adLabel} ad? You're in the right place.`
      : "Responding to our ad? You're in the right place."
    : null;
  const codeParam = (params.get("code") || "").trim().toUpperCase();
  useEffect(() => {
    if (!codeParam) return;
    const key = `cca_tracked_${codeParam}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: codeParam }),
    }).catch(() => {});
  }, [codeParam]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} data-testid="opportunity-hero" className="relative min-h-[92svh] overflow-hidden bg-ink text-paper">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMAGES.absArch} alt="Structured architecture" className="h-[118%] w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/65 to-ink" />
      </motion.div>

      <div className="relative mx-auto flex min-h-[92svh] max-w-[1400px] flex-col justify-center px-6 pt-28 pb-16 md:px-12 lg:px-16">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }} className="flex items-center gap-3">
          <span className="h-1.5 w-1.5 rounded-full bg-clay" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-invtext">The CCA Opportunity</span>
        </motion.div>
        {adNote && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            data-testid="hero-ad-note"
            className="mt-3 font-mono text-[11px] tracking-[0.06em] text-clay"
          >
            {adNote}
          </motion.p>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="mt-8 max-w-4xl font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.25rem]"
        >
          You've Mastered Selling. Now Learn the Rest of the Game.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
          className="mt-6 font-display text-xl font-medium tracking-tight text-clay sm:text-2xl"
        >
          Build Your Own Business Using Our Systems and Tools.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
          className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-12"
        >
          <div className="space-y-4 leading-relaxed text-invtext md:col-span-7 md:text-lg">
            <p>You're already good at sales. That's why we're interested in you.</p>
            <p>But great sales skills alone don't automatically make a great business owner.</p>
            <p>
              Building a successful business requires much more: systems, prospecting processes,
              client acquisition, organization, financial discipline, technology, leadership,
              customer retention and the ability to build something that can grow.
            </p>
            <p className="text-paper">
              CCA teaches you the rest of the game. You bring the sales ability. We provide the
              business model, operating system, training, technology and ongoing support.
            </p>
          </div>
          <div className="md:col-span-5 md:flex md:items-end md:justify-end">
            <button
              type="button"
              onClick={scrollToQualify}
              data-testid="hero-see-if-you-qualify"
              className="group inline-flex items-center gap-2 border border-paper bg-paper px-7 py-4 font-mono text-[12px] uppercase tracking-[0.12em] text-ink transition-colors hover:bg-transparent hover:text-paper"
            >
              See If You Qualify
              <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* Reusable heading block */
function Head({ label, title, dark = false, id }) {
  return (
    <div id={id}>
      <Reveal>
        <SectionLabel dark={dark} num={label}>Opportunity</SectionLabel>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className={`mt-6 max-w-3xl font-display text-3xl font-medium leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl ${dark ? "text-paper" : "text-ink"}`}>
          {title}
        </h2>
      </Reveal>
    </div>
  );
}

export default function Opportunity() {
  return (
    <div data-testid="opportunity-page">
      <Seo
        title="Build Your Own Business Inside Our Business"
        description="CCA gives successful sales professionals an opportunity to build their own business using established systems, training, technology and ongoing support."
        path="/opportunity"
        keywords="sales business opportunity, independent business owner, commercial insurance pipeline, entrepreneurial sales professional"
      />

      <Hero />

      {/* 2. FOUNDATION */}
      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="01" title="Being Great at Sales Is Only the Beginning." />
          <Reveal delay={0.1}>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-12">
              <div className="space-y-5 leading-relaxed text-subtext md:col-span-8 md:col-start-5 md:text-lg">
                <p>A great salesperson knows how to create opportunities, build relationships, handle objections and close business.</p>
                <p>A successful business owner has to know how to do much more.</p>
                <p>You have to understand the economics of the business. Organize and manage competing priorities. Leverage technology. Protect processes. Lead people. And build something that can grow and operate beyond your individual production.</p>
                <p className="font-display text-xl font-medium tracking-tight text-ink">That's a different game.</p>
                <p>CCA was designed to help successful sales professionals learn the game and play to win.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. WHAT CCA TEACHES */}
      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="02" title="We Teach You the Rest of the Game." />
          <RevealGroup className="mt-14 grid grid-cols-1 gap-px bg-line sm:grid-cols-2 lg:grid-cols-3">
            {TEACHES.map((t, i) => (
              <RevealItem key={t} className="group flex items-start gap-4 bg-paper p-6 transition-colors hover:bg-white md:p-8">
                <span className="font-mono text-xs text-clay">{String(i + 1).padStart(2, "0")}</span>
                <p className="leading-relaxed text-ink">{t}</p>
              </RevealItem>
            ))}
          </RevealGroup>
          <Reveal delay={0.1}>
            <div className="mt-12 border-l-2 border-clay pl-6">
              <p className="font-display text-xl font-medium leading-snug tracking-tight text-ink md:text-2xl">
                We don't teach you how to sell. That's what you bring. We help you turn that ability into a business.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. BUILD YOUR OWN BUSINESS INSIDE OUR BUSINESS */}
      <section className="bg-ink py-24 text-paper md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="03" title="Build Your Own Business Inside Our Business." dark />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl leading-relaxed text-invtext md:text-lg">
              CCA provides the infrastructure that helps ensure your success. It's like a franchise
              only better because it doesn't cost you hundreds of thousands upfront. You operate as an
              independent business owner and independent contractor. You develop your own CCA client
              relationships and earn based on the business you develop. Best of all we show you how to scale.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px bg-inkline md:grid-cols-2">
            <Reveal className="bg-ink p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">CCA Provides</p>
              <ul className="mt-6 space-y-4">
                {CCA_PROVIDES.map((c) => (
                  <li key={c} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-navy" strokeWidth={1.75} />
                    <span className="text-paper">{c}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="bg-ink p-8 md:p-10" delay={0.1}>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">You Provide</p>
              <ul className="mt-6 space-y-4">
                {YOU_PROVIDE.map((c) => (
                  <li key={c} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-navy" strokeWidth={1.75} />
                    <span className="text-paper">{c}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-12 font-display text-xl font-medium tracking-tight md:text-2xl">
              You can build a business of your own, but you don't have to do it by yourself.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 5. START PART-TIME */}
      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12 lg:px-16">
          <div className="md:col-span-6">
            <Head label="04" title="You Don't Have to Quit Your Job." />
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 leading-relaxed text-subtext md:text-lg">
                <p>One of the advantages of this model is that you can begin building your CCA business while maintaining your existing income.</p>
                <p>There is no minimum number of hours you are required to work. You determine when and how you work. CCA measures results—not hours.</p>
              </div>
            </Reveal>
          </div>
          <Reveal className="md:col-span-5 md:col-start-8 md:self-center" delay={0.15}>
            <div className="border border-line bg-white p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">Your first objective</p>
              <p className="mt-4 font-display text-2xl font-medium leading-snug tracking-tight text-ink md:text-3xl">
                Build your first three paying clients within 90 days.
              </p>
              <p className="mt-5 leading-relaxed text-subtext">
                As your client base grows, you can decide when your CCA business is large enough to
                justify making a larger commitment.
              </p>
              <div className="mt-8 border-t border-line pt-6">
                <p className="font-display text-lg font-medium tracking-tight text-ink">
                  You are responsible for your schedule and your results.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. HOW THE BUSINESS WORKS */}
      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="05" title="Here's How the Model Works." />
          <div className="mt-14 flex flex-col items-stretch gap-0 md:flex-row md:items-stretch">
            {FLOW.map((f, i) => (
              <div key={f.label} className="flex flex-col md:flex-1 md:flex-row md:items-stretch">
                <Reveal delay={i * 0.06} className="flex-1">
                  <div className="h-full border border-line bg-white p-6">
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-clay">Step {i + 1}</p>
                    <p className="mt-3 font-display text-lg font-medium leading-tight tracking-tight text-ink">{f.label}</p>
                    <p className="mt-2 text-sm leading-relaxed text-subtext">{f.detail}</p>
                  </div>
                </Reveal>
                {i < FLOW.length - 1 && (
                  <div className="flex items-center justify-center py-2 md:px-2 md:py-0">
                    <ArrowDown className="h-5 w-5 text-clay md:hidden" />
                    <ArrowRight className="hidden h-5 w-5 text-clay md:block" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-12 font-display text-xl font-medium tracking-tight text-ink md:text-2xl">
              You are not paid for showing up. You are compensated for the business you develop.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 7. THE ECONOMICS */}
      <section className="bg-ink py-24 text-paper md:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12 lg:px-16">
          <div className="md:col-span-5">
            <Head label="06" title="You Can Do the Math." dark />
            <Reveal delay={0.1}>
              <p className="mt-6 leading-relaxed text-invtext md:text-lg">
                CCA does not make any income claims or guarantees. Your results depend on your ability
                to develop and retain clients, build and lead a team and grow your business. The
                expectation is that no one will ever have to lead and mentor more than 5 people. Here
                is the compensation structure. You can do the math.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <RevealGroup className="border-t border-inkline">
              {COMP.map((c) => (
                <RevealItem key={c.role} className="flex items-baseline justify-between border-b border-inkline py-6">
                  <div>
                    <p className="font-display text-xl font-medium tracking-tight text-paper">{c.role}</p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-invtext">{c.note}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-3xl font-medium tracking-tight text-clay md:text-4xl">{c.value}</p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-invtext">{c.sub}</p>
                  </div>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </div>
      </section>

      {/* 8. THE PATH */}
      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="07" title="There Is a Path." />
          <div className="mt-14 border-l border-line pl-8 md:pl-10">
            {PATH.map((p, i) => (
              <Reveal key={p.role} delay={i * 0.05}>
                <div className="relative pb-10 last:pb-0">
                  <span className="absolute -left-[2.6rem] top-1 flex h-4 w-4 items-center justify-center rounded-full border border-navy bg-paper md:-left-[3.1rem]">
                    <span className="h-1.5 w-1.5 rounded-full bg-navy" />
                  </span>
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-xs text-clay">{p.n}</span>
                    <h3 className="font-display text-2xl font-medium tracking-tight text-ink">{p.role}</h3>
                  </div>
                  <p className="mt-2 max-w-2xl leading-relaxed text-subtext">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-12 font-display text-xl font-medium tracking-tight text-ink md:text-2xl">
              You don't advance because of how long you've been here. You advance because of what you
              accomplish.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 9. WE'RE NOT LOOKING FOR EVERYONE (qualification) */}
      <section id="qualify" className="scroll-mt-20 border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="08" title="We're Not Looking for Everyone." />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl leading-relaxed text-subtext md:text-lg">
              We are looking for seasoned sales professionals with a demonstrated record of success.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px bg-line md:grid-cols-2">
            <Reveal className="bg-paper p-8 md:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-navy">You may be a fit if</p>
              <ul className="mt-6 space-y-4">
                {FIT.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-navy" strokeWidth={1.75} />
                    <span className="leading-relaxed text-ink">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="bg-paper p-8 md:p-10" delay={0.1}>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay">This is probably NOT for you if</p>
              <ul className="mt-6 space-y-4">
                {NOT_FIT.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <X className="mt-0.5 h-5 w-5 flex-shrink-0 text-clay" strokeWidth={1.75} />
                    <span className="leading-relaxed text-subtext">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.12} className="mt-12">
            <QualifyQuiz />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-12 font-display text-xl font-medium tracking-tight text-ink md:text-2xl">
              We're deliberately selective. That's the point.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 10. WHY SELECTIVE */}
      <section className="bg-ink py-24 text-paper md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="09" title="We Can Afford to Be Selective." dark />
          <Reveal delay={0.1}>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-12">
              <div className="space-y-5 leading-relaxed text-invtext md:col-span-8 md:col-start-5 md:text-lg">
                <p>CCA does not need thousands of Associates to build a successful company.</p>
                <p>Our model is designed around developing capable business owners who can build their own client bases and, eventually, help develop other successful Associates. That allows us to focus on quality rather than quantity.</p>
                <p>We would rather have a small number of exceptional Associates than a large number of people who are not committed to building a business.</p>
                <p className="font-display text-xl font-medium tracking-tight text-paper">
                  We aren't trying to convince you to join us. We're trying to determine whether you belong here.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 11. THE FIRST STEP */}
      <section id="apply" className="scroll-mt-20 border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12 lg:px-16">
          <div className="md:col-span-5">
            <Head label="10" title="Think You Belong Here?" />
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 leading-relaxed text-subtext md:text-lg">
                <p>We don't ask for a résumé. A résumé tells us where you've worked. We want to know who you are, what you've accomplished and why you believe you have what it takes to build a business.</p>
                <p>If you believe you may be a fit, complete the short form and tell us why we should consider you.</p>
              </div>
            </Reveal>
          </div>
          <Reveal className="md:col-span-6 md:col-start-7" delay={0.15}>
            <ApplyForm />
          </Reveal>
        </div>
      </section>

      {/* 12. WHAT HAPPENS NEXT */}
      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Head label="11" title="What Happens After You Contact Us?" />
          <RevealGroup className="mt-14 grid grid-cols-1 gap-px bg-line md:grid-cols-3">
            {NEXT.map((s) => (
              <RevealItem key={s.n} className="bg-paper p-8 md:p-10">
                <p className="font-display text-4xl font-medium tracking-tight text-clay">{s.n}</p>
                <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-ink">{s.title}</h3>
                <p className="mt-3 leading-relaxed text-subtext">{s.body}</p>
              </RevealItem>
            ))}
          </RevealGroup>
          <Reveal delay={0.1}>
            <p className="mt-12 leading-relaxed text-subtext md:text-lg">
              There is no obligation to join. And there is no guarantee that we will invite you to join.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
